// Pure text and tone for the Providers tab. Every string is the approved copy (n12 MOCK-README).
import type {
  UsageAccount,
  UsageComputer,
  UsageSpend,
  UsageWindow,
  SwitchFailure,
} from "./provider-usage-types.js";
import type { SwitchState } from "./provider-usage-state.svelte.js";

export interface FormatCtx {
  now: number;
  /** IANA zone; undefined = the viewer's own. */
  timeZone?: string;
}
export type BarTone = "accent" | "attention" | "failed";
export type RefreshOutcome = "updated" | "no_answer" | "offline";
export interface RefreshAllKpi {
  computerCount: "1" | "2-3" | "4-10" | "11+";
  outcome: "all_answered" | "some_no_answer" | "some_offline";
}

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

export function barTone(pct: number): BarTone {
  if (pct >= 100) return "failed";
  return pct >= 80 ? "attention" : "accent";
}

/** A window whose reset time has passed: its reading belongs to the window that ended. */
export function hasReset(w: UsageWindow, now: number): boolean {
  return w.resetsAt !== null && w.resetsAt <= now;
}

/** The window as of `now`: once it has reset, its old percentage and status are no longer known. */
export function liveWindow(w: UsageWindow, now: number): UsageWindow {
  return hasReset(w, now) ? { ...w, usedPct: null, status: "unknown" } : w; // null: the wire shape's "no reading"
}

/** With `now`, a window past its reset is no longer at its limit; without it, the reading as observed. */
export function isLimitReached(w: UsageWindow, now?: number): boolean {
  if (now !== undefined && hasReset(w, now)) return false;
  return w.status === "rejected" || (w.usedPct ?? 0) >= 100;
}

/** One order for a computer's accounts, whatever order they arrived in: active first, then provider, label, key. */
export function sortAccounts(accounts: readonly UsageAccount[]): UsageAccount[] {
  const text = (a: string, b: string) => {
    if (a === b) return 0;
    return a < b ? -1 : 1;
  };
  return accounts.toSorted(
    (a, b) =>
      Number(b.active) - Number(a.active) ||
      text(a.provider, b.provider) ||
      text(a.label ?? "\uffff", b.label ?? "\uffff") ||
      text(a.accountKey, b.accountKey),
  );
}

export function usedText(w: UsageWindow): string {
  if (w.usedPct === undefined || w.usedPct === null) return "—";
  return `${Math.round(w.usedPct)}% used`;
}

function clock(ts: number, timeZone: string | undefined, zone = false): string {
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone,
    ...(zone ? { timeZoneName: "short" as const } : {}),
  }).format(ts);
}

function part(ts: number, timeZone: string | undefined, opts: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat("en-US", { timeZone, ...opts }).format(ts);
}

function relative(ms: number): string {
  const total = Math.max(1, Math.round(ms / MIN));
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h === 0 ? `${m} min` : `${h} h ${String(m).padStart(2, "0")} min`;
}

function futureReset(w: UsageWindow, now: number): number | undefined {
  return w.resetsAt !== null && w.resetsAt > now ? w.resetsAt : undefined;
}

function weeklyList(at: number, tz: string | undefined): string {
  return `${part(at, tz, { weekday: "short" })} ${clock(at, tz)}`;
}

export function resetsList(w: UsageWindow, ctx: FormatCtx): string {
  const at = futureReset(w, ctx.now);
  if (at === undefined) return "";
  const when =
    w.kind === "five_hour" ? `in ${relative(at - ctx.now)}` : weeklyList(at, ctx.timeZone);
  if (isLimitReached(w, ctx.now)) return `Limit reached · resets ${when}`;
  if (w.kind === "five_hour") return `Resets ${when} (${clock(at, ctx.timeZone)})`;
  return `Resets ${when}`;
}

/** The offline line: "Offline. Last known: weekly 90% used, resets Mon 9:00 AM." */
export function offlineLastKnown(w: UsageWindow | undefined, ctx: FormatCtx): string {
  if (!w || w.usedPct === null || w.usedPct === undefined) return "";
  const used = isLimitReached(w, ctx.now) ? 100 : Math.round(w.usedPct);
  const at = futureReset(w, ctx.now);
  const reset = at === undefined ? "" : `, resets ${weeklyList(at, ctx.timeZone)}`;
  return `Offline. Last known: weekly ${used}% used${reset}.`;
}

export function resetsDetail(w: UsageWindow, ctx: FormatCtx): string {
  const at = futureReset(w, ctx.now);
  if (at === undefined) return "";
  const time = clock(at, ctx.timeZone, true);
  if (w.kind === "five_hour") return `Resets in ${relative(at - ctx.now)} · ${time}`;
  const day = part(at, ctx.timeZone, { weekday: "short", month: "short", day: "numeric" });
  return `Resets ${day.replace(",", "")} · ${time}`;
}

/** The "Weekly" cell of the switcher table: "58% · resets Sun". */
export function weeklyShort(window: UsageWindow, ctx: FormatCtx): string {
  const w = liveWindow(window, ctx.now);
  const pct = w.usedPct === null ? "—" : `${Math.round(w.usedPct)}%`;
  const at = futureReset(w, ctx.now);
  if (at === undefined) return pct;
  return `${pct} · resets ${part(at, ctx.timeZone, { weekday: "short" })}`;
}

/** Two days or more shows the date ("Oct 27"): the approved copy has no "N days ago". */
export function ageText(at: number, now: number, timeZone?: string): string {
  const diff = Math.max(0, now - at);
  if (diff < MIN) return "just now";
  if (diff < HOUR) return `${Math.floor(diff / MIN)} min ago`;
  if (diff < DAY) return `${Math.floor(diff / HOUR)} h ago`;
  if (diff < 2 * DAY) return "yesterday";
  return part(at, timeZone, { month: "short", day: "numeric" });
}

export function updatedText(at: number, now: number): string {
  return `Updated ${ageText(at, now)}`;
}

export function offlineText(dataAsOf: number | null | undefined, timeZone?: string): string {
  if (dataAsOf === null || dataAsOf === undefined) return "Offline";
  return `Offline · data from ${clock(dataAsOf, timeZone)}`;
}

export function noAnswerText(dataAsOf: number | null | undefined, timeZone?: string): string {
  if (dataAsOf === null || dataAsOf === undefined) return "Didn't answer";
  return `Didn't answer: showing data from ${clock(dataAsOf, timeZone)}`;
}

export function providerName(provider: string): string {
  return provider.charAt(0).toUpperCase() + provider.slice(1);
}

/** Wire plan id ("max_5x") to its display name ("Max 5x"); an unknown id is only title-cased. */
export function planLabel(plan: string | null | undefined): string | undefined {
  const words = (plan ?? "").split(/[_\-\s]+/).filter((w) => w !== "");
  if (words.length === 0) return undefined;
  return words
    .map((w) =>
      /^\d+x$/i.test(w) ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
    )
    .join(" ");
}

export function accountTitle(a: UsageAccount): string {
  const label = a.label ?? (a.authKind === "api_key" ? "API key" : a.switcherName);
  return label ? `${providerName(a.provider)} · ${label}` : providerName(a.provider);
}

export function formatUsd(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v);
}

/** Where an API key's spend was read from (SPEC §4c labels). */
export function spendScopeText(scope: UsageSpend["scope"]): string {
  return scope === "all_sessions" ? "All sessions on this computer" : "Agent sessions only";
}

/** "412 K tokens", "1.2 M tokens"; "" under a thousand. */
export function formatTokens(n: number): string {
  if (n < 1_000) return "";
  if (n < 999_500) return `${Math.round(n / 1_000)} K tokens`;
  const m = n / 1_000_000;
  return `${m >= 100 ? Math.round(m) : Math.round(m * 10) / 10} M tokens`;
}

/** "1.2 M tokens · 52 sessions" or "· 1 session". */
export function spendWeekLine(spend: UsageSpend): string {
  const sessions = spend.sessions === 1 ? "1 session" : `${spend.sessions} sessions`;
  return [formatTokens(spend.weekTokens), sessions].filter((s) => s !== "").join(" · ");
}

function ownerPart(c: UsageComputer): string | undefined {
  if (c.mine || !c.ownerName) return undefined;
  return `owned by ${c.ownerName}`;
}

function lastSeenPart(c: UsageComputer, now: number): string | undefined {
  if (c.online || c.lastSeenAt === null) return undefined;
  return `last seen ${ageText(c.lastSeenAt, now)}`;
}

/** A computer nothing has been read from yet: "0 accounts" there would be a guess. */
export function isUnread(c: UsageComputer): boolean {
  return c.dataAsOf === null && c.accounts.length === 0;
}

export function computerMeta(c: UsageComputer, now: number): string {
  const n = c.accounts.length;
  const count = n === 1 ? "1 account" : `${n} accounts`;
  const switchers = [
    ...new Set(c.accounts.flatMap((a) => (a.switcher && a.switcherName ? [a.switcher] : []))),
  ];
  return [
    ownerPart(c),
    isUnread(c) ? undefined : count,
    c.mine && switchers.length > 0 ? `switcher: ${switchers.join(", ")}` : undefined,
    lastSeenPart(c, now),
  ]
    .filter((s): s is string => s !== undefined)
    .join(" · ");
}

function countBucket(n: number): RefreshAllKpi["computerCount"] {
  if (n === 1) return "1";
  if (n <= 3) return "2-3";
  return n <= 10 ? "4-10" : "11+";
}

function worstOutcome(results: RefreshOutcome[]): RefreshAllKpi["outcome"] {
  if (results.includes("offline")) return "some_offline";
  return results.includes("no_answer") ? "some_no_answer" : "all_answered";
}

export function refreshAllKpi(results: RefreshOutcome[]): RefreshAllKpi | undefined {
  if (results.length === 0) return undefined;
  const worst = worstOutcome(results);
  return { computerCount: countBucket(results.length), outcome: worst };
}

export function switchAccountName(a: UsageAccount): string {
  return a.label ?? a.switcherName ?? providerName(a.provider);
}

/** A Claude result line ("Now using X", "still using X") holds only while the rows still say so. */
export function resultLineHolds(s: SwitchState, c: UsageComputer): boolean {
  if (s.kind !== "done" || s.provider === "codex") return true;
  const active = c.accounts.filter((a) => a.provider === s.provider && a.active);
  if (active.length === 0) return true; // no row claims Active (a re-read that failed): nothing contradicts it
  // "still using A" holds only while A is the Active one; "Now using X" only while X is.
  const name = s.stillUsing ?? s.name;
  const isNamed = (a: UsageAccount) => switchAccountName(a) === name || a.label === name || a.switcherName === name; // prettier-ignore
  return active.some((a) => isNamed(a) || (s.stillUsing === undefined && a.accountKey === s.accountKey)); // prettier-ignore
}

export function canSwitch(c: UsageComputer, a: UsageAccount): boolean {
  if (!c.mine || a.active || a.authKind === "api_key" || a.switcherName?.startsWith("-"))
    return false;
  return a.switcher === "claude-swap" || a.switcher === "codex-account";
}

export function isLastSwitched(c: UsageComputer, a: UsageAccount): boolean {
  return c.facts?.lastSwitchedAccountKey === a.accountKey;
}

/** codex-account can't say which account is live, so after a switch the live row stops claiming it. */
export function liveRowSwitchedAway(c: UsageComputer, a: UsageAccount): boolean {
  return a.provider === "codex" && a.active && c.facts?.lastSwitchedAccountKey !== undefined;
}

export function switchConfirmBody(
  provider: "claude" | "codex",
  n: number,
  computer: string,
  account: string,
): string {
  const one = n === 1;
  const running = `${n} ${one ? "agent is" : "agents are"} running on ${computer}.`;
  if (provider === "codex") {
    const keeps = one
      ? "It keeps the old account until its next start."
      : "They keep the old account until their next start.";
    return `Codex: ${running} ${keeps} New agents use ${account}.`;
  }
  const uses = one
    ? `It uses ${account} from its next message.`
    : `They use ${account} from their next message.`;
  return `Claude: ${running} ${uses} Work in progress is not stopped.`;
}

const SWITCH_REASON: Record<SwitchFailure, (switcher: string) => string> = {
  not_installed: (s) => `${s} is not installed on this computer`,
  timed_out: (s) => `${s} did not answer in 30 seconds`,
  could_not_switch: (s) => `${s} could not switch`,
  no_change: (s) => `${s} finished without changing the Codex sign-in`,
  binary_writable: (s) => `Other users can change ${s}, so Cyborg did not run it`,
  binary_in_workspace: (s) => `${s} is inside an agent's working folder, so Cyborg did not run it`,
};

export interface SwitchFailedFacts {
  switcher: string;
  reason: SwitchFailure;
  provider: string;
  active: string | null;
  answered: boolean;
  unchanged: boolean;
}

export function switchFailedBody(f: SwitchFailedFacts): string {
  const reason = `${SWITCH_REASON[f.reason](f.switcher)}.`;
  // It was stopped mid-way, so nothing about the account is known until the next read.
  if (f.reason === "timed_out")
    return `${reason} Cyborg stopped it. Refresh to check which account is active.`;
  if (!f.answered || f.reason === "no_change") return reason;
  if (f.provider === "codex")
    return f.unchanged ? `${reason} Your Codex account is unchanged.` : reason;
  return f.active ? `${reason} ${f.active} is still active.` : reason;
}
